import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { GameCard, GameCardSkeleton } from '../components/GameCard';
import HeroVisual from '../components/HeroVisual';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Zap, Shield, Tag, Headphones, Activity, Cpu, ArrowRight, Gamepad2, Phone, ShoppingBag } from 'lucide-react';
import theme from '../theme.config';

const COPY = theme.copy;
const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', name: 'All Services', icon: Activity, desc: 'Complete catalog' },
  { id: 'games', name: 'Game Top-up', icon: Gamepad2, desc: 'Instant game top-up' },
  { id: 'pulsa', name: 'Mobile Credits', icon: Phone, desc: 'Prepaid phone credits' },
  { id: 'voucher', name: 'Digital Vouchers', icon: ShoppingBag, desc: 'Instant digital vouchers' },
];

export default function Home() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [digiflazzBrands, setDigiflazzBrands] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredProduct, setFeaturedProduct] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, digiflazzBrands, activeCategory, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [seedRes, catalogRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/biller/catalog`),
      ]);

      if (seedRes.status === 'fulfilled') {
        setProducts(seedRes.value.data.products || []);
      }

      if (catalogRes.status === 'fulfilled' && catalogRes.value.data.success) {
        setDigiflazzBrands(catalogRes.value.data.brands || []);
      }

      try { await axios.post(`${API_URL}/api/seed`); } catch {}
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    const allItems = [];

    for (const brand of digiflazzBrands) {
      allItems.push({
        id: `df-${brand.slug}`,
        slug: `df/${brand.slug}`,
        name: brand.brand,
        image: brand.image,
        category: brand.category,
        source: 'digiflazz',
        itemCount: brand.items.length,
        startPrice: Math.min(...brand.items.map(i => i.price)),
      });
    }

    const dfNames = new Set(digiflazzBrands.map(b => b.brand.toLowerCase()));
    for (const p of products) {
      if (!dfNames.has(p.name.toLowerCase())) {
        const catMap = { game: 'games', voucher: 'voucher' };
        allItems.push({ ...p, category: catMap[p.category] || p.category, source: 'seed' });
      }
    }

    let filtered = [...allItems];

    if (activeCategory !== 'all') {
      filtered = filtered.filter(p => p.category === activeCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
    if (filtered.length > 0) {
      const randomIndex = Math.floor(Math.random() * filtered.length);
      setFeaturedProduct(filtered[randomIndex]);
    } else {
      setFeaturedProduct(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-50">
      {/* Background Dots Grid Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(#1c1c1f_1px,transparent_1px)] [background-size:16px_16px] opacity-20 pointer-events-none" />

      {/* ── HERO SECTION ── */}
      <section 
        className="relative overflow-hidden z-10 border-b border-zinc-900 pt-36 pb-20 bg-cover bg-center"
        style={{ backgroundImage: "url('/hero-bg.png')" }}
      >
        {/* Glow effects like in Reseller */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/70 via-transparent to-[#09090b]/95 pointer-events-none" />
        <div className="absolute top-10 left-10 w-72 h-72 bg-emerald-500/15 rounded-full blur-3xl animate-pulse pointer-events-none" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column - Headline and Search */}
            <div className="lg:col-span-7 text-left">
              {/* Version Badge */}
              <div className="inline-flex items-center gap-2 mb-6 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-mono text-[10px] text-emerald-400 font-semibold tracking-wider uppercase">NeuroVanta Gateway v1.2.4</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 leading-[1.1]">
                {COPY.hero.titleLine1} <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500">{COPY.hero.titleLine2}</span>
              </h1>

              {/* Subtitle */}
              <p className="text-zinc-400 text-sm sm:text-base md:text-lg mb-8 max-w-xl leading-relaxed">
                {COPY.hero.subtitle}
              </p>

              {/* Clean Input Search bar */}
              <div className="relative max-w-md shadow-2xl">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  type="text"
                  placeholder={COPY.hero.searchPlaceholder}
                  className="pl-10 pr-4 py-6 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-500 rounded-xl focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/40 text-sm font-mono"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Right Column - Earth Synapse Animation & Visualizer */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <HeroVisual className="w-full max-w-md" />
            </div>

          </div>
        </div>
      </section>

      {/* ── SPOTLIGHT BOOSTER BANNER ── */}
      {!loading && featuredProduct && (
        <section className="py-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-b border-zinc-900">
          <div className="relative overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-r from-zinc-900 to-emerald-950/20 p-6 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
            <div className="flex items-center gap-4">
              {featuredProduct.image ? (
                <img 
                  src={featuredProduct.image} 
                  alt={featuredProduct.name} 
                  className="w-12 h-12 rounded-lg object-cover border border-zinc-800 shadow-md"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center font-bold text-emerald-400 text-lg shadow-inner">
                  ★
                </div>
              )}
              <div>
                <span className="text-[9px] font-mono text-emerald-400 tracking-wider uppercase bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">Featured Product</span>
                <h3 className="font-bold text-white text-md mt-1 uppercase tracking-wide">{featuredProduct.name}</h3>
                <p className="text-xs text-zinc-400 mt-0.5 max-w-xl">
                  {featuredProduct.category === 'games' 
                    ? `Get your top-up instantly with our fully automated gateway. Quick, secure, and always active.`
                    : `Purchase vouchers and mobile credits instantly. Fast delivery, secure transactions, and 24/7 support.`
                  }
                </p>
              </div>
            </div>
            <Button
              className="bg-zinc-50 hover:bg-zinc-200 text-zinc-950 font-semibold text-xs py-4 px-6 rounded-lg whitespace-nowrap self-stretch md:self-auto flex items-center justify-center gap-2 group transition-all"
              onClick={() => navigate(`/product/${featuredProduct.slug}`)}
            >
              Top Up Now
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </section>
      )}

      {/* ── FEATURES GRID ── */}
      <section className="py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 border-b border-zinc-900">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {(() => {
            const featureIcons = [Cpu, Zap, Shield, Headphones];
            return COPY.features.map((f, idx) => {
              const Icon = featureIcons[idx % featureIcons.length];
              return (
                <div key={idx} className="flex gap-3 text-left">
                  <div className="w-10 h-10 rounded-lg border border-zinc-800 bg-zinc-900/50 flex items-center justify-center flex-shrink-0 text-emerald-400">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-xs uppercase tracking-wider">{f.label}</h4>
                    <p className="text-xs text-zinc-400 mt-1">{f.desc}</p>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </section>

      {/* ── PRODUCT SECTIONS ── */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Modern Tabs Category selector */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 border-b border-zinc-900 pb-6">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  className={`px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all border ${
                    isActive
                      ? 'bg-zinc-800 border-zinc-700 text-white'
                      : 'bg-transparent border-transparent text-zinc-400 hover:text-white'
                  }`}
                  onClick={() => setActiveCategory(cat.id)}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.name}
                </button>
              );
            })}
          </div>

          {!loading && (
            <span className="font-mono text-xs text-zinc-500">
              {filteredProducts.length} ACTIVE PRODUCT{filteredProducts.length !== 1 ? 'S' : ''}
            </span>
          )}
        </div>

        {/* Dynamic Card Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {[...Array(10)].map((_, i) => <GameCardSkeleton key={i} />)}
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {filteredProducts.map((product) => (
              <GameCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 border border-zinc-800 rounded-xl bg-zinc-900/20">
            <p className="font-mono text-xs text-zinc-500">No active products found matching your search</p>
          </div>
        )}
      </section>

      {/* ── RESELLER ONBOARDING CALLOUT (CTA) ── */}
      <section className="py-16 border-t border-zinc-900 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tight text-white mb-4">
              Join the <span className="text-emerald-400">NeuroVanta</span> Network
            </h2>
            <p className="text-zinc-400 text-sm mb-8 max-w-xl mx-auto leading-relaxed">
              Unlock competitive reseller pricing and automate your game top-up business with our Earth-synapse infrastructure.
            </p>
            <Button
              className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs py-4 px-6 rounded-lg transition-colors"
              onClick={() => navigate('/reseller')}
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
